/**
 * ChallengeTile — collapsible "this week's challenge" surface on Home.
 *
 * iter-72 (e): nudges users into the active community Challenge without
 * stealing focus from the primary "feeling" textarea. Auto-collapses if
 * the user has already seen this challenge slug (localStorage) — so it
 * doesn't pester on every visit.
 *
 * Renders nothing if no active challenges exist.
 *
 * Single-tile design: shows the most-recently-created active challenge.
 * Tapping "join" navigates to /challenge/<slug> for the full gallery.
 * Tapping "write" preloads the prompt into the textarea via callback.
 */
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { listChallenges } from "../lib/api";
import { track } from "../lib/track";

const SEEN_KEY = "moodcard_challenge_tile_seen_v1";

export default function ChallengeTile({ onUsePrompt }) {
    const [challenge, setChallenge] = useState(null);
    const [expanded, setExpanded] = useState(true);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        let alive = true;
        listChallenges()
            .then((items) => {
                if (!alive) return;
                if (!Array.isArray(items) || items.length === 0) return;
                const top = items[0];
                setChallenge(top);
                // If user has dismissed this exact slug already, start collapsed.
                try {
                    const seen = JSON.parse(localStorage.getItem(SEEN_KEY) || "[]");
                    if (Array.isArray(seen) && seen.includes(top.slug)) {
                        setExpanded(false);
                    }
                } catch { /* ignore corrupt storage */ }
                track("challenge_tile_seen", { slug: top.slug });
            })
            .catch(() => { /* no-op — feature is optional */ });
        return () => { alive = false; };
    }, []);

    const markSeen = (slug) => {
        try {
            const seen = JSON.parse(localStorage.getItem(SEEN_KEY) || "[]");
            if (!seen.includes(slug)) {
                seen.push(slug);
                localStorage.setItem(SEEN_KEY, JSON.stringify(seen.slice(-20)));
            }
        } catch { /* ignore */ }
    };

    if (!challenge || dismissed) return null;

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="mb-5 rounded-2xl border border-amber-300/15 bg-amber-400/5 overflow-hidden"
            data-testid="home-challenge-tile"
        >
            <button
                type="button"
                onClick={() => {
                    if (expanded) markSeen(challenge.slug);
                    setExpanded((s) => !s);
                }}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-amber-400/8 transition-colors"
                aria-expanded={expanded}
                data-testid="home-challenge-tile-toggle"
            >
                <div className="flex items-center gap-2.5 min-w-0">
                    <Sparkles className="w-3.5 h-3.5 text-amber-300 shrink-0" />
                    <span className="text-[10px] tracking-[0.22em] uppercase text-amber-200/85 shrink-0">
                        this week
                    </span>
                    <span className="text-app font-serif italic truncate">
                        {challenge.title}
                    </span>
                </div>
                <span className={`text-amber-200/70 text-[11px] tracking-[0.18em] transition-transform shrink-0 ${expanded ? "rotate-180" : ""}`}>▾</span>
            </button>

            <AnimatePresence initial={false}>
                {expanded && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden"
                    >
                        <div className="px-4 pb-4 pt-1">
                            <p className="font-serif italic text-app-2 text-sm leading-snug">
                                "{challenge.prompt}"
                            </p>
                            <div className="mt-3 flex items-center flex-wrap gap-2">
                                {onUsePrompt && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            track("challenge_tile_use_prompt", { slug: challenge.slug });
                                            markSeen(challenge.slug);
                                            onUsePrompt(challenge.prompt, challenge.slug);
                                        }}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-400 hover:bg-amber-300 text-black text-[11px] tracking-[0.12em] uppercase font-medium transition-colors"
                                        data-testid="home-challenge-use-prompt"
                                    >
                                        write your version
                                        <ArrowRight className="w-3 h-3" />
                                    </button>
                                )}
                                <Link
                                    to={`/challenge/${challenge.slug}`}
                                    onClick={() => track("challenge_tile_open", { slug: challenge.slug })}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-amber-300/25 hover:border-amber-300/45 text-amber-200/85 hover:text-amber-100 text-[11px] tracking-[0.12em] uppercase transition-colors"
                                    data-testid="home-challenge-open"
                                >
                                    see {challenge.submission_count || 0} entries
                                </Link>
                                <button
                                    type="button"
                                    onClick={() => {
                                        markSeen(challenge.slug);
                                        setDismissed(true);
                                        track("challenge_tile_dismiss", { slug: challenge.slug });
                                    }}
                                    aria-label="dismiss"
                                    className="ml-auto w-7 h-7 rounded-full text-app-3 hover:text-app-2 hover:bg-app/5 flex items-center justify-center transition-colors"
                                    data-testid="home-challenge-dismiss"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
}
